/**
 * WEG-Tab für PropertyDetail — Phase 1 Fundament.
 *
 * Zeigt für ein WEG-/Mixed-Objekt:
 *   - MEA-Bilanz (Summe der weg_share aller Einheiten vs. weg_total_shares)
 *   - Eigentümer-Liste (aus units.owner_contact_id aggregiert)
 *   - Verwaltungsbeirat (bearbeitbar)
 *   - Einheit-für-Einheit Übersicht mit MEA + Sondereigentum-Flag + Eigentümer
 *
 * Die Einheit-Bearbeitung (MEA/Sondereigentum/Owner setzen) läuft über den
 * bestehenden Unit-Dialog in PropertyDetail — hier zeigen wir Read-Only-Übersicht +
 * Deep-Link zum Bearbeiten.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Warning, CheckCircle, Scales, Users, UserPlus, X } from "@phosphor-icons/react";
import BeschlussSammlung from "@/components/property/BeschlussSammlung";
import WirtschaftsplanSection from "@/components/property/WirtschaftsplanSection";
import HausgeldSection from "@/components/property/HausgeldSection";
import AssemblySection from "@/components/property/AssemblySection";
import AnnualStatementSection from "@/components/property/AnnualStatementSection";

const contactName = (c) =>
  c?.display_name ||
  [c?.first_name, c?.last_name].filter(Boolean).join(" ").trim() ||
  c?.company ||
  "(unbenannt)";

export default function WEGPropertyTab({ property, onChange }) {
  const [summary, setSummary] = useState(null);
  const [ownerContacts, setOwnerContacts] = useState([]);
  const [beiratOpen, setBeiratOpen] = useState(false);
  const [beiratPick, setBeiratPick] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get(`/weg/${property.id}/summary`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
    api.get("/contacts", { params: { role: "owner", limit: 500 } }).then((r) => setOwnerContacts(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, [property.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const beiratCandidates = useMemo(() => {
    const existing = new Set((summary?.beirat || []).map((b) => b.id));
    return ownerContacts.filter((c) => !existing.has(c.id));
  }, [ownerContacts, summary]);

  const addBeirat = async () => {
    if (!beiratPick) return;
    const ids = [...(summary?.beirat || []).map((b) => b.id), beiratPick];
    await saveBeirat(ids);
    setBeiratPick("");
    setBeiratOpen(false);
  };
  const removeBeirat = async (cid) => {
    const ids = (summary?.beirat || []).map((b) => b.id).filter((x) => x !== cid);
    await saveBeirat(ids);
  };
  const saveBeirat = async (ids) => {
    setSaving(true);
    try {
      await api.put(`/weg/${property.id}/beirat`, { contact_ids: ids });
      toast.success("Verwaltungsbeirat gespeichert");
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  if (!summary) return <div className="text-sm text-slate-500">Lade WEG-Daten …</div>;

  const meaComplete = summary.mea_complete;
  const meaSum = summary.mea_sum;
  const meaTotal = summary.property.weg_total_shares;
  const meaDiff = meaTotal - meaSum;

  return (
    <div className="space-y-6" data-testid="weg-property-tab">
      {/* Kachel: MEA-Bilanz + Warnungen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className={`rounded-lg border p-4 ${meaComplete ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-center gap-2 text-xs uppercase font-semibold tracking-wide text-slate-600 mb-1">
            <Scales size={14} /> Miteigentumsanteile
          </div>
          <div className="text-2xl font-semibold" data-testid="weg-mea-sum">
            {meaSum} <span className="text-slate-400">/</span> {meaTotal}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            Nenner ({meaTotal}) wird in Stammdaten &gt; Bearbeiten geändert
          </div>
          <div className={`mt-1 text-xs flex items-center gap-1 ${meaComplete ? "text-emerald-700" : "text-amber-700"}`}>
            {meaComplete ? (
              <>
                <CheckCircle size={13} weight="fill" /> Vollständig
              </>
            ) : (
              <>
                <Warning size={13} weight="fill" />
                {meaDiff > 0 ? `${meaDiff} MEA fehlen` : `${Math.abs(meaDiff)} MEA zu viel`}
              </>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 text-xs uppercase font-semibold tracking-wide text-slate-600 mb-1">
            <Users size={14} /> Eigentümer
          </div>
          <div className="text-2xl font-semibold text-slate-900">{summary.owners.length}</div>
          <div className="mt-1 text-xs text-slate-500">
            {summary.units.length} Einheit{summary.units.length === 1 ? "" : "en"}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 text-xs uppercase font-semibold tracking-wide text-slate-600 mb-1">
            <Users size={14} /> Verwaltungsbeirat
          </div>
          <div className="text-2xl font-semibold text-slate-900">{summary.beirat.length}</div>
          <Button
            size="sm" variant="outline" className="mt-2 h-7 text-xs"
            data-testid="weg-beirat-manage-btn"
            onClick={() => setBeiratOpen(true)}
          >
            Beirat verwalten
          </Button>
        </div>
      </div>

      {/* Warnungen */}
      {summary.warnings?.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="weg-warnings">
          <div className="font-medium mb-1 flex items-center gap-1.5"><Warning size={14} weight="fill" /> Hinweise</div>
          <ul className="list-disc pl-5 space-y-0.5 text-xs">
            {summary.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Beirat-Liste */}
      {summary.beirat.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-2">Verwaltungsbeirat</h3>
          <div className="flex flex-wrap gap-2">
            {summary.beirat.map((b) => (
              <Badge key={b.id} variant="secondary" className="gap-1.5 pl-2.5 pr-1 py-1" data-testid={`weg-beirat-${b.id}`}>
                {contactName(b)}
                <button
                  onClick={() => removeBeirat(b.id)}
                  className="ml-1 rounded hover:bg-slate-200 p-0.5"
                  disabled={saving}
                  aria-label={`${contactName(b)} entfernen`}
                >
                  <X size={11} />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Einheiten-Tabelle */}
      <div>
        <h3 className="text-sm font-medium text-slate-700 mb-2">Einheiten &amp; Miteigentumsanteile</h3>
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Einheit</th>
                <th className="text-left px-3 py-2">Wohnfläche</th>
                <th className="text-right px-3 py-2">MEA</th>
                <th className="text-center px-3 py-2">Typ</th>
                <th className="text-left px-3 py-2">Eigentümer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {summary.units.map((u) => {
                const ow = summary.owners.find((o) => o.id === u.owner_contact_id);
                return (
                  <tr key={u.id} data-testid={`weg-unit-row-${u.unit_number}`}>
                    <td className="px-3 py-2 font-mono text-xs">{u.unit_number || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{u.living_area ? `${u.living_area} m²` : "—"}</td>
                    <td className="px-3 py-2 text-right font-medium">
                      {u.weg_share
                        ? `${u.weg_share}/${meaTotal}`
                        : <span className="text-amber-600">nicht gesetzt</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {u.is_sondereigentum !== false ? (
                        <Badge variant="outline" className="text-[10px] font-normal">Sondereigentum</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] font-normal bg-slate-100">Gemeinschaft</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {ow ? contactName(ow) : <span className="text-amber-600 text-xs">— nicht zugewiesen</span>}
                    </td>
                  </tr>
                );
              })}
              {summary.units.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500 text-sm">Noch keine Einheiten angelegt.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Zum Bearbeiten der MEA und Eigentümer-Zuordnung im Tab <Link to={`?tab=units`} className="text-emerald-700 hover:underline">Einheiten</Link> die jeweilige Einheit öffnen.
        </p>
      </div>

      {/* Phase 2 — Beschluss-Sammlung */}
      <div className="border-t border-slate-200 pt-6">
        <BeschlussSammlung property={property} />
      </div>

      {/* Phase 3 — Wirtschaftspläne */}
      <div className="border-t border-slate-200 pt-6">
        <WirtschaftsplanSection property={property} />
      </div>

      {/* Phase 4 — Hausgeld-Vorschüsse */}
      <div className="border-t border-slate-200 pt-6">
        <HausgeldSection property={property} />
      </div>

      {/* Phase 5 — Eigentümerversammlung */}
      <div className="border-t border-slate-200 pt-6">
        <AssemblySection property={property} />
      </div>

      {/* Phase 6 — Jahresabrechnung */}
      <div className="border-t border-slate-200 pt-6">
        <AnnualStatementSection property={property} />
      </div>

      {/* Beirat-Dialog */}
      <Dialog open={beiratOpen} onOpenChange={setBeiratOpen}>
        <DialogContent data-testid="weg-beirat-dialog">
          <DialogHeader>
            <DialogTitle>Verwaltungsbeirat verwalten</DialogTitle>
            <DialogDescription>
              Wähle einen Eigentümer aus, der in den Verwaltungsbeirat aufgenommen werden soll.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={beiratPick} onValueChange={setBeiratPick}>
              <SelectTrigger data-testid="weg-beirat-select">
                <SelectValue placeholder="Eigentümer wählen …" />
              </SelectTrigger>
              <SelectContent>
                {beiratCandidates.length === 0 ? (
                  <div className="text-sm text-slate-500 p-3">Alle Eigentümer bereits im Beirat.</div>
                ) : (
                  beiratCandidates.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{contactName(c)}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBeiratOpen(false)}>Abbrechen</Button>
            <Button onClick={addBeirat} disabled={!beiratPick || saving} data-testid="weg-beirat-add-btn">
              <UserPlus size={14} className="mr-1.5" /> Aufnehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
